import { formatUserFacingError, withErrorCode } from '@/lib/errors'
import { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Button } from '@/components/ui/button'
import { Separator } from '@/components/ui/separator'
import { useCatalog } from '../hooks/useCatalog'
import { useServiceCards } from '../hooks/useServiceCards'
import { useQuoteCalculator, computeServiceBaseCost, computeAddonCost } from '../hooks/useQuoteCalculator'
import { useHireableMembers } from '../hooks/useHireableMembers'
import { requestService } from '../services/requestService'
import { IdentitySection } from './IdentitySection'
import { ServiceCard } from './ServiceCard'
import { QuoteSection } from './QuoteSection'
import { SubmissionSection } from './SubmissionSection'
import { useAuth } from '@/hooks/useAuth'
import type { DiscountCategory, ServiceRequestAddonItem, ChoreographerRef } from '@/types'
import type { IdentityFormValues, SubmissionFormValues } from '../types'

// ─── Schemas ────────────────────────────────────────────────────────────────

const identitySchema = z.object({
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
  email: z.string().email('Enter a valid email address'),
  organizationName: z.string(),
})

const submissionSchema = z.object({
  additionalInfo: z.string(),
  tosAccepted: z
    .boolean()
    .refine((v) => v === true, { message: 'You must accept the Terms of Service' }),
  bookingAcknowledge: z
    .boolean()
    .refine((v) => v === true, { message: 'Please acknowledge this before submitting' }),
  mailingOptIn: z.enum(['yes', 'no']),
})

const DISCOUNT_PERCENT: Record<DiscountCategory, number> = {
  none: 0,
  student: 10,
  nonprofit: 10,
  odd_artist: 20,
}

interface RequestFormProps {
  onSuccess: () => void
  initialService?: string
  initialOffering?: string
  initialServiceKey?: string
}

export function RequestForm({ onSuccess, initialService, initialOffering, initialServiceKey }: RequestFormProps) {
  const { user } = useAuth()
  const { catalog, loading: catalogLoading, error: catalogError } = useCatalog()
  const {
    cards,
    addCard,
    removeCard,
    updateCard,
    updateCardService,
    updateCardOffering,
    prefillCard,
    updateAddon,
  } = useServiceCards()
  const { members: hireableMembers } = useHireableMembers()
  const [selectedProviders, setSelectedProviders] = useState<ChoreographerRef[]>([])
  const [discountCategory, setDiscountCategory] = useState<DiscountCategory>('none')
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [prefillApplied, setPrefillApplied] = useState(false)

  useEffect(() => {
    if (!catalog || prefillApplied) return
    if (!initialOffering && !initialService && !initialServiceKey) {
      setPrefillApplied(true)
      return
    }

    const cardId = cards[0]!.id

    if (initialOffering) {
      const offering = catalog.offerings.find((o) => o.id === initialOffering)
      // Resolve serviceKey in this order:
      //   1) offering doc's own serviceKey
      //   2) `sk` URL param
      //   3) the category whose title matches the offering's category field
      //   4) the category whose title matches the `service` URL param
      const serviceKeyFromCategory = offering
        ? catalog.allCategories.find(
            (c) => c.title.toLowerCase() === offering.category.toLowerCase()
          )?.serviceKey
        : undefined
      const serviceKeyFromUrlCategory = initialService
        ? catalog.allCategories.find(
            (c) => c.title.toLowerCase() === initialService.toLowerCase()
          )?.serviceKey
        : undefined
      const serviceKey =
        offering?.serviceKey ||
        initialServiceKey ||
        serviceKeyFromCategory ||
        serviceKeyFromUrlCategory
      if (serviceKey && offering) {
        prefillCard(cardId, serviceKey, offering.id, catalog)
        setPrefillApplied(true)
        return
      }
      // Offering not in catalog (non-bookable pricing), still prefill service type if we have the key
      if (serviceKey) {
        updateCardService(cardId, serviceKey)
        setPrefillApplied(true)
        return
      }
    }

    // Final fallback: match service type by category title from URL
    const serviceSource = initialServiceKey
      ? catalog.services.find((s) => s.serviceKey === initialServiceKey)
      : initialService
        ? catalog.services.find((s) => s.title.toLowerCase() === initialService.toLowerCase())
        : undefined

    if (serviceSource) {
      updateCardService(cardId, serviceSource.serviceKey)
    }

    setPrefillApplied(true)
  }, [catalog, initialOffering, initialService, initialServiceKey, prefillApplied, cards, prefillCard, updateCardService])

  const identityForm = useForm<IdentityFormValues>({
    resolver: zodResolver(identitySchema),
    defaultValues: { firstName: '', lastName: '', email: '', organizationName: '' },
  })

  // Prefill identity fields from the logged-in user's profile
  useEffect(() => {
    if (!user) return
    const [first = '', ...rest] = (user.displayName ?? '').split(' ')
    identityForm.setValue('firstName', first, { shouldValidate: false })
    identityForm.setValue('lastName', rest.join(' '), { shouldValidate: false })
    identityForm.setValue('email', user.email ?? '', { shouldValidate: false })
  }, [user]) // eslint-disable-line react-hooks/exhaustive-deps

  const submissionForm = useForm<SubmissionFormValues>({
    resolver: zodResolver(submissionSchema),
    defaultValues: {
      additionalInfo: '',
      tosAccepted: false,
      bookingAcknowledge: false,
      mailingOptIn: 'no',
    },
  })

  const quote = useQuoteCalculator(cards, catalog, discountCategory)

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()

    const [identityValid, submissionValid] = await Promise.all([
      identityForm.trigger(),
      submissionForm.trigger(),
    ])

    if (!identityValid || !submissionValid) return

    // Require at least one complete service selection
    const hasService = cards.some((c) => c.selectedOfferingId)
    if (!hasService) {
      setSubmitError(withErrorCode('Please select at least one service before submitting.', 'app/validation'))
      return
    }

    // Enforce 72-hour minimum lead time on any event service that has date+time.
    const minDateMs = Date.now() + 72 * 60 * 60 * 1000
    const tooSoon = cards.find((c) => {
      if (!c.selectedOfferingId) return false
      const offering = catalog?.offerings.find((o) => o.id === c.selectedOfferingId)
      if (!offering?.isEvent) return false
      if (!c.date || !c.time) return false
      const bookingMs = new Date(`${c.date}T${c.time}`).getTime()
      return !isNaN(bookingMs) && bookingMs < minDateMs
    })
    if (tooSoon) {
      setSubmitError(
        withErrorCode(
          'One or more services are scheduled less than 72 hours from now. ' +
            'Please pick a date+time at least 3 days out, or contact us directly for short-notice bookings.',
          'request/lead-time',
        ),
      )
      return
    }

    setSubmitError(null)
    setIsSubmitting(true)

    try {
      const identity = identityForm.getValues()
      const submission = submissionForm.getValues()

      const services = cards
        .filter((c) => c.selectedOfferingId && catalog)
        .map((c) => {
          const offering = catalog!.offerings.find((o) => o.id === c.selectedOfferingId)!

          const baseCost = computeServiceBaseCost(c, catalog!)

          const addons: ServiceRequestAddonItem[] = c.addons
            .filter((a) => {
              if (a.selectionMode === 'boolean') return a.boolValue
              if (a.selectionMode === 'integer') return a.intValue > 0
              if (a.selectionMode === 'string') return !!a.stringValue
              return false
            })
            .map((a) => ({
              addonId: a.addonId,
              label: a.label,
              selectionMode: a.selectionMode,
              value:
                a.selectionMode === 'boolean'
                  ? a.boolValue
                  : a.selectionMode === 'integer'
                    ? a.intValue
                    : a.stringValue,
              lineCost: computeAddonCost(a, c),
            }))

          const addonTotal = addons.reduce((sum, a) => sum + a.lineCost, 0)
          const lineSubtotal = baseCost + addonTotal

          return {
            offeringId: c.selectedOfferingId,
            serviceKey: c.selectedServiceKey,
            subType: offering.title,
            ...(offering.isEvent && c.date ? { date: c.date } : {}),
            ...(offering.isEvent && c.time ? { time: c.time } : {}),
            ...(offering.isEvent && c.location ? { location: c.location } : {}),
            ...(offering.priceUnit === 'perHour' ? { hours: c.hours } : {}),
            ...(offering.priceUnit === 'perMinute' ? { filmLengthMinutes: c.filmLengthMinutes } : {}),
            ...(offering.priceUnit === 'perHeadshot' ? { headshots: c.headshots } : {}),
            ...(offering.hasComplexity ? { complexity: c.complexity } : {}),
            ...(offering.showAudioOption ? { includeAudioRecording: c.includeAudioRecording } : {}),
            addons,
            lineSubtotal,
          }
        })

      await requestService.submitRequest({
        firstName: identity.firstName,
        lastName: identity.lastName,
        email: identity.email.trim().toLowerCase(),
        ...(identity.organizationName.trim() ? { organizationName: identity.organizationName.trim() } : {}),
        discountCategory,
        discountAppliedPercent: DISCOUNT_PERCENT[discountCategory],
        mailingOptIn: submission.mailingOptIn === 'yes',
        services,
        quoteTotal: quote.formattedTotal,
        additionalInfo: submission.additionalInfo,
        requestedProviders: selectedProviders.map((p) => p.name).join(', '),
        tosAccepted: submission.tosAccepted,
        bookingAcknowledge: submission.bookingAcknowledge,
      })

      onSuccess()
    } catch (err) {
      setSubmitError(
        formatUserFacingError(err, 'Something went wrong. Please try again or email us directly.')
      )
    } finally {
      setIsSubmitting(false)
    }
  }

  if (catalogLoading) {
    return (
      <div className="py-16 text-center text-muted-foreground">
        Loading service catalog…
      </div>
    )
  }

  if (catalogError ?? !catalog) {
    return (
      <div className="rounded-xl border border-destructive/30 bg-destructive/5 p-6 space-y-2">
        <p className="font-semibold text-destructive">
          {withErrorCode('Service requests are temporarily unavailable.', 'request/catalog-unavailable')}
        </p>
        <p className="text-sm text-muted-foreground">
          {catalogError ?? withErrorCode('Our services catalog could not be loaded.', 'request/catalog-unavailable')}
          {' '}Please email{' '}
          <a href="mailto:info@onedaydance.com" className="underline hover:text-foreground">
            info@onedaydance.com
          </a>{' '}
          with your request while we resolve this.
        </p>
      </div>
    )
  }

  return (
    <div className="lg:grid lg:grid-cols-[1fr_300px] lg:items-start lg:gap-10">
      {/* ── Left: form fields ── */}
      <form onSubmit={(e) => void handleSubmit(e)} noValidate className="space-y-10">
        {/* Identity + discount */}
        <IdentitySection
          register={identityForm.register}
          errors={identityForm.formState.errors}
          discountValue={discountCategory}
          onDiscountChange={setDiscountCategory}
          isLoggedIn={!!user}
        />

        <Separator />

        {/* Service cards */}
        <section className="space-y-4">
          <h2 className="text-xl font-semibold">Services Requested</h2>
          <div className="space-y-4">
            {cards.map((card, idx) => (
              <ServiceCard
                key={card.id}
                card={card}
                catalog={catalog}
                cardIndex={idx}
                canRemove={cards.length > 1}
                onServiceChange={(sk) => updateCardService(card.id, sk)}
                onOfferingChange={(oid) => updateCardOffering(card.id, oid, catalog)}
                onFieldChange={(patch) => updateCard(card.id, patch)}
                onAddonChange={(addonId, patch) => updateAddon(card.id, addonId, patch)}
                onRemove={() => removeCard(card.id)}
              />
            ))}
          </div>
          <Button type="button" variant="outline" onClick={addCard}>
            + Add another service
          </Button>
        </section>

        <Separator />

        {/* Estimate — mobile only (desktop shows in sticky sidebar) */}
        <div className="lg:hidden">
          <QuoteSection quote={quote} discountCategory={discountCategory} />
        </div>

        <Separator className="lg:hidden" />

        {/* Submission */}
        <SubmissionSection
          register={submissionForm.register}
          errors={submissionForm.formState.errors}
          isSubmitting={isSubmitting}
          submitError={submitError}
          formattedTotal={quote.formattedTotal}
          selectedProviders={selectedProviders}
          onProvidersChange={setSelectedProviders}
          hireableMembers={hireableMembers}
        />
      </form>

      {/* ── Right: sticky estimate — desktop only ── */}
      <div className="hidden lg:block lg:sticky lg:top-24">
        <QuoteSection quote={quote} discountCategory={discountCategory} />
      </div>
    </div>
  )
}
